<script setup lang="ts">
/**
 * Lightweight spotlight tour engine. Highlights one element per step
 * (dimmed backdrop with a cutout), navigates between routes as steps demand,
 * and never blocks the demoer from clicking through the app mid-tour (the
 * backdrop is pointer-events-none; only the step card captures clicks).
 *
 * TWO TOURS RUN ON THIS ONE COMPONENT. The step list is a prop, so each tour is
 * a data file and nothing more:
 *   • `demoTourSteps.ts`   — Prosser's 14-step sales walkthrough, demo mode only
 *   • `../tour/launchTourSteps.ts` — the client's first-login tour, live account
 * They share the renderer and nothing else. Changing one must never require
 * touching the other; if a change here would alter the sales tour's behaviour,
 * it belongs behind a prop with the sales tour's current value as the default
 * (`chromeSelector` is the one such prop today).
 *
 * A step whose selector is '' — or whose element isn't found — has no cutout to
 * spotlight. Those steps render the card PARKED IN THE TOP-RIGHT with NO
 * backdrop at all, so the page being described stays fully visible and
 * unobstructed (Dugger, 2026-07-31, after walking the live tour page by page:
 * "its right in the middle kind of blocking the page we are talking about",
 * then the same complaint again on Safety Scores, Accidents and Roadside
 * Inspections — "its greyed behind"). Ten of the fourteen steps are
 * selector-less, so this is the common case, not an edge case.
 *
 * Rejected along the way, do not reintroduce: centring the card (blocks the
 * content), dimming these steps (hides the product being sold), and pinning the
 * card over the staff chrome band at full width (read as a banner, not a card).
 *
 * The top offset is measured off the demo banner rather than hard-coded: the
 * chrome band's height changes with the toggles in it and it wraps on narrow
 * viewports. Falls back to a plain top-of-viewport pin if the banner is absent.
 *
 * Plan: knowledge/portal/plans/2026-07-16-portal-demo-mode.md
 */
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import type { DemoTourStep } from './demoTourSteps'

// The element a parked card sits BELOW. Default = the staff demo banner, which
// is the last row of the chrome band whenever the sales tour is running. The
// first-login tour (client-support Phase 3A) runs with no banner on screen and
// passes the portal header instead — see `chromeSelector` below. Nothing else
// about this component is tour-specific: the step list is a prop, so a second
// tour is a second `steps` array, never a fork of this file.
const DEMO_CHROME_SELECTOR = '[data-testid="demo-mode-banner"]'

const props = withDefaults(
  defineProps<{
    active: boolean
    steps: DemoTourStep[]
    /**
     * Selector for the chrome a selector-less card parks under. May match
     * SEVERAL elements (e.g. a layout with a desktop header AND a mobile one,
     * one of which is `display:none` at any width) — `measureIntroTop` takes the
     * lowest visible match, so a hidden duplicate cannot park the card at the
     * top of the viewport, on top of the header that IS showing.
     */
    chromeSelector?: string
  }>(),
  { chromeSelector: DEMO_CHROME_SELECTOR },
)
const emit = defineEmits<{ 'update:active': [boolean] }>()

const router = useRouter()
const idx = ref(0)
const rect = ref<DOMRect | null>(null)

// Guarded current step for the template (strict noUncheckedIndexedAccess).
const currentStep = computed<DemoTourStep | undefined>(() => props.steps[idx.value])

async function goTo(i: number) {
  if (i < 0 || i >= props.steps.length) return end()
  // Close the OUTGOING step's drill-in before anything else, so a demoer never
  // lands on the next page with a drawer still hanging open behind the card.
  await closeSeeMore()
  idx.value = i
  const step = props.steps[i]
  if (!step) return end()
  if (router.currentRoute.value.path !== step.route) {
    await router.push(step.route)
  }
  await nextTick()
  // The chrome band is shell-level and already painted, so fix the park offset
  // NOW — the 450ms wait below is for the destination VIEW, and waiting it out
  // would paint the card at the fallback offset (over the chrome) first, then
  // visibly drop it into place.
  measureIntroTop()
  // Let the target view fetch/render before measuring.
  setTimeout(measure, 450)
}

// ── Top-right park (every selector-less / missing-anchor step) ───────────────
// Only the vertical offset needs measuring — horizontally the card just hugs the
// right gutter. The reference is `chromeSelector`'s bottom edge: for the sales
// tour that is the demo banner, the last element of the staff chrome band, which
// always exists while that tour is running (it can only be started from a button
// inside it).
const GUTTER = 24 // px from the viewport's right edge and below the chrome

const introTop = ref<number>(GUTTER)

function measureIntroTop() {
  // Lowest VISIBLE match. Zero-height boxes are responsive duplicates that are
  // currently `display:none` (the portal ships a desktop header and a mobile
  // one); measuring those would park the card over whichever header is actually
  // painted. With a single always-visible element — the demo banner — this is
  // the max of one, i.e. exactly the previous behaviour.
  let bottom: number | null = null
  for (const el of document.querySelectorAll(props.chromeSelector)) {
    const b = el.getBoundingClientRect()
    if (b.height <= 0) continue
    if (bottom === null || b.bottom > bottom) bottom = b.bottom
  }
  // Clamp: if the chrome has scrolled off (negative bottom) the card parks near
  // the top of the viewport rather than off-screen above it.
  introTop.value = bottom !== null ? Math.max(bottom + 12, GUTTER) : GUTTER
}

// ── "See more" drill-in ──────────────────────────────────────────────────────
// Opt-in deep dive per step: opens the real surface (an in-app route, or a click
// that opens a drawer) instead of adding more linear steps. Leaving the step
// closes it again.
const seeMoreOpen = ref(false)

// True only for a drill-in that opened a PrimeVue overlay (a `click` target),
// as opposed to one that just navigated (`route`). Measured behaviour with the
// accidents drawer open, 2026-07-31 — do not re-guess these numbers:
//   • the drawer MASK computes to z-index 11002 (PrimeVue's ZIndex utility uses
//     an ~11000 base, NOT the 1100 the config names) and covers the whole
//     viewport, so it swallows every click aimed at the card;
//   • the drawer PANEL occupies x=900..1440 of a 1440 viewport — precisely the
//     right gutter the card parks in.
// So an overlay drill-in needs BOTH a z-index above the mask and a flip to the
// LEFT gutter, or the card is invisible behind the panel and unclickable under
// the mask (which is exactly how the first cut failed).
const drillIsOverlay = computed(() => seeMoreOpen.value && !!currentStep.value?.seeMore?.click)

// The opened panel's box, so the card can park in whichever gutter it leaves and
// shrink to fit. Without this the card is merely "on the left", which clears a
// right-anchored drawer but still clips a CENTRED dialog (the §396.9(d) certify
// modal overlapped by ~30px and cut off its own title).
// `.p-dialog`/`.p-drawer` cover PrimeVue; `[data-tour-panel]` is the opt-in hook
// for hand-rolled overlays (the Team-training detail modal).
const PANEL_SELECTOR = '.p-dialog, .p-drawer, [data-tour-panel]'
const panelBox = ref<{ left: number; right: number } | null>(null)

function measurePanel() {
  if (!seeMoreOpen.value) {
    panelBox.value = null
    return
  }
  const el = document.querySelector(PANEL_SELECTOR)
  const b = el?.getBoundingClientRect()
  panelBox.value = b && b.width > 0 ? { left: b.left, right: b.right } : null
}

// Gutter the card should occupy while drilled in: the roomier side of the panel,
// with the card shrunk to fit it. Null => no panel measured, keep the normal park.
const drillPark = computed<{ side: 'left' | 'right'; width: number } | null>(() => {
  const p = panelBox.value
  if (!seeMoreOpen.value || !p) return null
  const vw = window.innerWidth
  const leftRoom = p.left
  const rightRoom = vw - p.right
  const side = leftRoom >= rightRoom ? 'left' : 'right'
  const room = Math.max(leftRoom, rightRoom)
  // 28rem normally; never wider than the gutter minus its two margins, and never
  // so narrow the card is unreadable — below that we accept a small overlap
  // rather than shipping a sliver.
  const width = Math.max(280, Math.min(448, room - GUTTER * 2))
  return { side, width }
})

async function openSeeMore() {
  const s = currentStep.value?.seeMore
  if (!s || seeMoreOpen.value) return
  if (s.route) {
    await router.push(s.route)
  } else if (s.click) {
    const el = document.querySelector<HTMLElement>(s.click)
    // Target absent (empty register / no rows for this client) — do nothing
    // rather than throw, and leave the button available in case data arrives.
    if (!el) return
    el.click()
  }
  seeMoreOpen.value = true
  // A drill-in surface owns the screen; drop any cutout so the spotlight's
  // box-shadow can't grey out the thing we just opened.
  rect.value = null
  await nextTick()
  measureIntroTop()
  // Panels animate in (PrimeVue ~300ms), so measure once they've settled —
  // measuring on this tick would read a mid-transition box, or none at all.
  measurePanel()
  setTimeout(measurePanel, 400)
}

// `returnToStep` is true when the demoer closes the drill-in explicitly and
// expects to land back on the step's own page; false when goTo() is about to
// navigate somewhere else anyway, so we skip the wasted hop (and its flash).
async function closeSeeMore(returnToStep = false) {
  if (!seeMoreOpen.value) return
  const step = currentStep.value
  seeMoreOpen.value = false
  panelBox.value = null
  if (step?.seeMore?.click) {
    // Prefer an explicit close control when the step names one — hand-rolled
    // overlays don't necessarily listen for Escape (the Team-training detail
    // modal doesn't). Otherwise Escape, which every PrimeVue drawer/dialog honours.
    const closer = step.seeMore.close
      ? document.querySelector<HTMLElement>(step.seeMore.close)
      : null
    if (closer) closer.click()
    else document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }))
    await nextTick()
  } else if (step?.seeMore?.route && returnToStep && router.currentRoute.value.path !== step.route) {
    await router.push(step.route)
    await nextTick()
  }
  if (returnToStep) {
    // Restore this step's normal park/cutout, which measure() refused to touch
    // while the drill-in was open.
    measureIntroTop()
    setTimeout(measure, 450)
  }
}

function measure() {
  const step = props.steps[idx.value]
  // Never re-measure while a drill-in is open: the surface is the point, and a
  // stray cutout would dim it.
  if (seeMoreOpen.value) return
  if (!step?.selector) {
    rect.value = null // no cutout — card parks top-right
    measureIntroTop()
    return
  }
  const el = document.querySelector(step.selector)
  if (el) {
    el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    setTimeout(() => {
      rect.value = el.getBoundingClientRect()
    }, 350)
  } else {
    // Anchor missing (e.g. a client removed that grid widget) — degrade to the
    // same top-right parked card rather than breaking the tour.
    rect.value = null
    measureIntroTop()
  }
}

function end() {
  // Ending mid-drill-in must not leave the drawer open behind the closed tour.
  void closeSeeMore()
  rect.value = null
  emit('update:active', false)
}

watch(
  () => props.active,
  (on) => {
    if (on) void goTo(0)
  },
)

const onResize = () => {
  if (!props.active) return
  // While drilled in, measure() is a no-op by design — re-measure the panel so
  // the card's gutter and width follow the resize.
  if (seeMoreOpen.value) {
    measureIntroTop()
    measurePanel()
    return
  }
  measure()
}
window.addEventListener('resize', onResize)
onBeforeUnmount(() => window.removeEventListener('resize', onResize))

// ── Step-card placement ──────────────────────────────────────────────────────
// With a cutout showing, pin the card OUTSIDE the spotlight so it never covers
// the section it points at (Dugger: the bottom-centre pin sat right on top of the
// bottom-row widgets). `rect` is the measured TARGET element; the spotlight div
// is drawn 8px larger — the box-shadow dim rides on it — so we add that pad + a
// margin here rather than measuring the padded div.
const cardEl = ref<HTMLElement | null>(null)
const cardPos = ref<{ top: number; left: number } | null>(null)

function positionCard() {
  const r = rect.value
  const el = cardEl.value
  if (!r || !el) {
    cardPos.value = null
    return
  }
  const PAD = 8 // the spotlight's visual overhang beyond the target rect
  const GAP = 16 // breathing room between the spotlight and the card
  const EDGE = 16 // min viewport edge margin
  const vw = window.innerWidth
  const vh = window.innerHeight
  const cb = el.getBoundingClientRect()
  const cw = cb.width
  const ch = cb.height

  // Visual cutout bounds (target rect + the spotlight overhang).
  const top = r.top - PAD
  const bottom = r.bottom + PAD
  const left = r.left - PAD
  const right = r.right + PAD
  const cx = r.left + r.width / 2 // horizontal centre of the target
  const cy = r.top + r.height / 2 // vertical centre of the target

  const clampX = (x: number) => Math.max(EDGE, Math.min(x, vw - EDGE - cw))
  const clampY = (y: number) => Math.max(EDGE, Math.min(y, vh - EDGE - ch))

  // Four candidate placements in preference order (below → above → right →
  // left). Each carries the free space available in its direction and the card
  // dimension it needs there; every coord is clamped fully inside the viewport.
  const candidates = [
    { place: () => ({ top: clampY(bottom + GAP), left: clampX(cx - cw / 2) }), room: vh - EDGE - (bottom + GAP), need: ch },
    { place: () => ({ top: clampY(top - GAP - ch), left: clampX(cx - cw / 2) }), room: top - GAP - EDGE, need: ch },
    { place: () => ({ top: clampY(cy - ch / 2), left: clampX(right + GAP) }), room: vw - EDGE - (right + GAP), need: cw },
    { place: () => ({ top: clampY(cy - ch / 2), left: clampX(left - GAP - cw) }), room: left - GAP - EDGE, need: cw },
  ]

  // First candidate whose room fits the card wins. If NONE fits (a pathological
  // target that ~fills the viewport, or a very short viewport), fall to the one
  // with the MOST room and clamp it on-screen: it may then graze the padded
  // cutout edge, but it never sits centred on top of the target the way the old
  // bottom-centre pin did. cardPos stays non-null for every set rect — only the
  // rect==null (centred intro card) branch leaves it null.
  const fits = candidates.find((c) => c.room >= c.need)
  const chosen = fits ?? candidates.reduce((a, b) => (b.room > a.room ? b : a))
  cardPos.value = chosen.place()
}

const cardClass = computed(() => {
  if (!rect.value) {
    // Selector-less / missing-anchor: parked top-right, out of the content's way.
    // No translate — cardStyle sets top + right directly.
    return 'w-[28rem] max-w-[calc(100vw-2rem)] transition-all duration-300'
  }
  if (cardPos.value) {
    // Smart-placed beside the cutout; animate the move between steps/resizes.
    return 'w-[26rem] max-w-[calc(100vw-2rem)] transition-all duration-300'
  }
  // Pre-placement transient only (rect set, positionCard not yet run this tick).
  // Never painted — the cardPos update flushes before paint — so this is just a
  // sane default; the real pathological case is clamped on-screen by positionCard.
  return 'bottom-8 left-1/2 w-[26rem] max-w-[calc(100vw-2rem)] -translate-x-1/2'
})

const cardStyle = computed(() => {
  if (!rect.value) {
    // Parked in a gutter, `right`/`left` (not a computed x) so it hugs at any
    // width. While drilled in it takes the roomier side of the opened panel and
    // shrinks to fit (drillPark). With no panel measured — a `route` drill-in —
    // it still moves LEFT, because CSA Insights puts its own controls, including
    // the score simulator, in the top right.
    const p = drillPark.value
    if (p) {
      return p.side === 'left'
        ? { top: `${introTop.value}px`, left: `${GUTTER}px`, width: `${p.width}px` }
        : { top: `${introTop.value}px`, right: `${GUTTER}px`, width: `${p.width}px` }
    }
    return seeMoreOpen.value
      ? { top: `${introTop.value}px`, left: `${GUTTER}px` }
      : { top: `${introTop.value}px`, right: `${GUTTER}px` }
  }
  return cardPos.value
    ? { top: `${cardPos.value.top}px`, left: `${cardPos.value.left}px` }
    : undefined
})

// Re-place the card whenever the cutout target changes: step changes fire this
// via measure() setting rect; window resize re-runs measure() → rect → here.
// nextTick lets the card render at its cutout width before we measure it, and the
// cardPos update flushes in the same tick so the bottom-centre default is never
// painted (no flash before the smart position lands).
watch(rect, async (r) => {
  if (!r) {
    cardPos.value = null
    return
  }
  await nextTick()
  positionCard()
})
</script>

<template>
  <Teleport to="body">
    <!-- z-900 sits ABOVE the portal shell chrome (sidebar z-30, sticky headers
         z-10) so the dim covers the whole app, but BELOW PrimeVue's overlay
         (1000) / modal (1100+) layers AND the Action Center's ReviewWalkthrough
         (z-1000). That lets any drawer/dialog the demoer opens mid-tour — the
         activity detail drawer, the review walkthrough — float ABOVE the dim
         instead of being buried under it. -->
    <!-- z-900 normally (see above). While an overlay drill-in is open the whole
         overlay is raised above PrimeVue's measured mask z-index (11002) so the
         card stays visible AND clickable on top of it — safe only because a
         drill-in forces rect=null, so there is no dim or cutout that could grey
         out the surface we just opened. A `route` drill-in has no mask, so it
         keeps the normal layer. -->
    <div
      v-if="active"
      class="pointer-events-none fixed inset-0"
      :class="drillIsOverlay ? 'z-[11500]' : 'z-[900]'"
    >
      <!-- Spotlight: a transparent box whose huge box-shadow dims everything else -->
      <div
        v-if="rect"
        class="absolute rounded-lg transition-all duration-300"
        data-testid="demo-tour-spotlight"
        :style="{
          top: `${rect.top - 8}px`,
          left: `${rect.left - 8}px`,
          width: `${rect.width + 16}px`,
          height: `${rect.height + 16}px`,
          boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.62)',
        }"
      />
      <!-- No dim element at all any more. A cutout step dims via the spotlight's
           box-shadow above; a selector-less step is deliberately undimmed so the
           page it describes stays visible (Dugger, 2026-07-31). -->

      <!-- Step card. With a cutout showing it's smart-placed OUTSIDE the spotlight
           (below → above → beside → bottom-centre fallback) so it never covers the
           section it points at; selector-less steps park in the top-right. -->

      <div
        ref="cardEl"
        class="pointer-events-auto absolute rounded-xl bg-white p-5 shadow-2xl"
        :class="cardClass"
        :style="cardStyle"
        data-testid="demo-tour-card"
      >
        <p class="mb-1 text-xs font-semibold uppercase tracking-wide text-primary-600">
          {{ idx + 1 }} / {{ steps.length }}
        </p>
        <h3 class="mb-2 text-lg font-semibold text-secondary-900">{{ currentStep?.title }}</h3>
        <p class="mb-4 text-sm leading-relaxed text-secondary-700">{{ currentStep?.body }}</p>
        <a
          v-if="currentStep?.cta"
          :href="currentStep.cta.href"
          target="_blank"
          rel="noopener"
          class="mb-4 inline-flex items-center gap-1.5 rounded-lg border border-primary-300 bg-primary-50 px-3 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-100"
          data-testid="demo-tour-cta"
        >
          <i class="pi pi-external-link text-xs" />
          {{ currentStep.cta.label }}
        </a>
        <!-- Opt-in drill-in. Swaps to a "Close" affordance once open so the
             demoer can back out without advancing the tour. -->
        <button
          v-if="currentStep?.seeMore"
          type="button"
          class="mb-4 inline-flex items-center gap-1.5 rounded-lg border border-primary-300 bg-primary-50 px-3 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-100"
          data-testid="demo-tour-see-more"
          @click="seeMoreOpen ? closeSeeMore(true) : openSeeMore()"
        >
          <i class="pi text-xs" :class="seeMoreOpen ? 'pi-times' : 'pi-arrow-right'" />
          {{ seeMoreOpen ? 'Close' : currentStep.seeMore.label }}
        </button>
        <div class="flex items-center justify-between">
          <button
            type="button"
            class="text-xs text-secondary-500 hover:text-secondary-800"
            data-testid="demo-tour-end"
            @click="end"
          >
            End tour
          </button>
          <div class="flex gap-2">
            <button
              v-if="idx > 0"
              type="button"
              class="rounded-lg border border-secondary-300 px-3 py-1.5 text-sm text-secondary-700 hover:bg-secondary-50"
              @click="goTo(idx - 1)"
            >
              Back
            </button>
            <button
              type="button"
              class="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
              data-testid="demo-tour-next"
              @click="goTo(idx + 1)"
            >
              {{ idx === steps.length - 1 ? 'Finish' : 'Next' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
